import { Component, EventEmitter, Input, Output, ChangeDetectorRef, OnDestroy, OnInit, inject } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { interval, Observable, Subscription } from 'rxjs';
import {
  CheckOTPResultResponseResult,
  CreateOTPResultResponseResult,
  RegisterUserCompleteCommand,
  RequestEmailOtpModel,
  RequestPhoneOtpModel,
  UserProxy,
  ValidateEmailModel,
  ValidatePhoneModel,
} from '@/app/proxy/proxies';
import { AuthService } from '../../../shared/services/auth.service';
import { RegisterationSteps } from '@/app/auth/shared/auth-enums';
import { extractBackendErrorMessage } from '@/app/auth/shared/auth-error.util';
import { OTP_RESEND_COUNTDOWN_SECONDS } from '@/app/shared/consts/otp-consts';
import { MixpanelTrackerService, MixpanelTimerKeys } from '@/app/core/analytics';

@Component({
  selector: 'auth-register-verify-otp',
  imports: [SharedModule],
  providers: [UserProxy],
  templateUrl: './verify-otp.component.html',
  styleUrl: './verify-otp.component.less',
})
export class VerifyOtpComponent implements OnInit, OnDestroy {
  private readonly _mixpanelTracker = inject(MixpanelTrackerService);

  @Output() steperChange = new EventEmitter<RegisterationSteps>();
  @Input() verifyOtpInfo: string = '';
  @Input() otpChannel: 'email' | 'phone' = 'email';
  @Input() progressValues: number[] = [100, 66, 0];

  otpValue: number | undefined;

  otpValidationStatus: boolean = true;

  resendCodeCounter: number = 0;
  resendCodeCounterSubscription: Subscription = new Subscription();

  registerUser: RegisterUserCompleteCommand | undefined = new RegisterUserCompleteCommand();

  backendErrorMessage: string = '';
  confirming: boolean = false;

  // Tracking counters for Mixpanel
  private resendCount = 0;
  private retryCount = 0;

  constructor(
    private _userProxy: UserProxy,
    private _authService: AuthService,
    private _cd: ChangeDetectorRef
  ) {
    this.registerUser = this._authService.getRegisterModal();
  }

  ngOnInit(): void {
    this.backendErrorMessage = '';
    this.startCountdownTimer();

    // Start timer for tracking time spent on code entry
    this._mixpanelTracker.startTimer(MixpanelTimerKeys.CODE_ENTRY);

    // Reset counters
    this.resendCount = 0;
    this.retryCount = 0;
  }

  ngOnDestroy(): void {
    if (this.resendCodeCounterSubscription) {
      this.resendCodeCounterSubscription.unsubscribe();
    }
  }

  get isEmailChannel(): boolean {
    return this.otpChannel === 'email';
  }

  get showResendHelpText(): boolean {
    return this.resendCount > 0;
  }

  backButton() {
    this.steperChange.emit(this.isEmailChannel ? RegisterationSteps.EMAIL : RegisterationSteps.PHONE);
  }

  confirmVerifyOtp() {
    if (this.confirming) {
      return;
    }

    // Increment retry count each time user clicks confirm
    this.retryCount++;
    this.confirming = true;

    this.validateOtp().subscribe({
      next: (res: CheckOTPResultResponseResult) => {
        if (res.isSuccess && res.result?.isValid) {
          this.handleValidationSuccess();
        } else {
          this.handleValidationFailure(res.errors);
        }
      },
      error: () => this.handleValidationFailure(),
    });
  }

  resendCodeTrigger() {
    this.requestOtp().subscribe({
      next: (res: CreateOTPResultResponseResult) => {
        if (res.isSuccess && res.result) {
          // Increment resend count for tracking
          this.resendCount++;
          this.startCountdownTimer();
        } else {
          this.backendErrorMessage = extractBackendErrorMessage(res.errors);
          this.otpValidationStatus = false;
          // detectChanges() renders synchronously and lets p-inputOtp write its
          // model signal mid-change-detection, which throws NG0600.
          this._cd.markForCheck();
        }
      },
      // The interceptor already toasts the failure; just leave the countdown unstarted.
      error: () => {
        this.otpValidationStatus = false;
        this._cd.markForCheck();
      },
    });
  }

  private validateOtp(): Observable<CheckOTPResultResponseResult> {
    if (this.isEmailChannel) {
      return this._userProxy.user_ValidateEmail(
        new ValidateEmailModel({
          email: this.registerUser?.email,
          otp: this.otpValue ?? 0,
        })
      );
    }

    return this._userProxy.user_ValidatePhone(
      new ValidatePhoneModel({
        phoneCode: this.registerUser?.phoneCode?.toString().replace(/\+/g, ''),
        phone: this.registerUser?.phone?.toString().replace(/-/g, ''),
        otp: this.otpValue ?? 0,
      })
    );
  }

  private requestOtp(): Observable<CreateOTPResultResponseResult> {
    if (this.isEmailChannel) {
      return this._userProxy.user_RequestEmailOtp(new RequestEmailOtpModel({ email: this.registerUser?.email }));
    }

    return this._userProxy.user_RequestPhoneOtp(
      new RequestPhoneOtpModel({
        phoneCode: this.registerUser?.phoneCode?.toString().replace(/\+/g, ''),
        phone: this.registerUser?.phone?.toString().replace(/-/g, ''),
      })
    );
  }

  private handleValidationSuccess() {
    // Track Code Entered event on successful OTP validation
    this._mixpanelTracker.trackCodeEntered({
      'entry point': 'Register',
      'code sent to': this.isEmailChannel ? 'Email' : 'Phone number',
      'resend count': this.resendCount,
      'retry count': this.retryCount - 1, // Subtract 1 because spec says "starting with 0"
      'phone number country': this.registerUser?.phoneCode || undefined,
      'time spent': this._mixpanelTracker.getElapsedTime(MixpanelTimerKeys.CODE_ENTRY),
    });

    // Carry the validated code so it can be submitted with the registration
    const validatedOtp = this.otpValue ?? 0;
    const registerModal = new RegisterUserCompleteCommand(this.registerUser?.toJSON());
    if (this.isEmailChannel) {
      registerModal.emailOtp = validatedOtp;
    } else {
      registerModal.phoneOtp = validatedOtp;
    }
    this._authService.setRegisterModal(registerModal);

    this.confirming = false;
    this.steperChange.emit(this.isEmailChannel ? RegisterationSteps.PASSWORD : RegisterationSteps.ABOUT);
  }

  private handleValidationFailure(errors?: { message?: string }[]) {
    this.confirming = false;
    this.backendErrorMessage = extractBackendErrorMessage(errors);
    this.otpValue = undefined;
    this.otpValidationStatus = false;
    // Clearing otpValue re-renders p-inputOtp; detectChanges() would make it
    // write its model signal mid-change-detection and throw NG0600.
    this._cd.markForCheck();
  }

  private startCountdownTimer(): void {
    // Start countdown timer
    this.resendCodeCounter = OTP_RESEND_COUNTDOWN_SECONDS;
    this.resendCodeCounterSubscription.unsubscribe();
    this.resendCodeCounterSubscription = interval(1000).subscribe(() => {
      this.resendCodeCounter--;
      if (this.resendCodeCounter <= 0) {
        this.resendCodeCounter = 0;
        this.resendCodeCounterSubscription.unsubscribe();
      }
    });
  }

  get formattedResendCodeCounter(): string {
    const minutes: number = Math.floor(this.resendCodeCounter / 60);
    const seconds: number = this.resendCodeCounter % 60;
    return `${this.padZero(minutes)}:${this.padZero(seconds)}`;
  }

  private padZero(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}
